// src/pages/GalleriesPhotos/GalleryCard.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import Button from "../../components/Button/Button";
import { deleteGallery } from "../../services/gallerieServices";
import EditAlbumModal from "./EditAlbumModal";
import { toast } from "react-toastify";
import {
  FaImages,
  FaTrashAlt,
  FaEdit,
  FaLock,
  FaUnlock,
} from "react-icons/fa";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const GalleryCard = ({ gallery, index, userRole, refreshGalleriesList }) => {
  const navigate = useNavigate();
  const defaultImage = "/img/default_image.jpg";
  const [editModalOpen, setEditModalOpen] = useState(false);

  const hasPhotos = Array.isArray(gallery.photos) && gallery.photos.length > 0;

  const settings = {
    dots: hasPhotos && gallery.photos.length > 1,
    infinite: hasPhotos && gallery.photos.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: hasPhotos && gallery.photos.length > 1,
    autoplaySpeed: 3000,
    arrows: false,
  };

  const handleViewAlbumClick = () => {
    navigate(`/galerie-photos/album/${gallery.id}`);
  };

  const handleDeleteGallery = async () => {
    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir supprimer définitivement cette galerie ?"
      )
    )
      return;

    try {
      await deleteGallery(gallery.id);
      toast.success("Galerie supprimée avec succès.");
      refreshGalleriesList();
    } catch (error) {
      console.error("Erreur lors de la suppression de la galerie:", error);
      toast.error("Erreur lors de la suppression de la galerie.");
    }
  };

  const isReversed = index % 2 === 1;
  const isAdmin = userRole === "Administrateur" || userRole === "Bureau";

  return (
    <article
      className={`group flex flex-col ${
        isReversed ? "md:flex-row-reverse" : "md:flex-row"
      } rounded-2xl overflow-hidden shadow-xl border border-slate-200/80 dark:border-slate-700/80 bg-white/95 dark:bg-slate-900/95`}
    >
      {/* Bloc image / slider */}
      <div className="relative md:w-1/2 bg-slate-900">
        {hasPhotos ? (
          gallery.photos.length > 1 ? (
            <Slider {...settings}>
              {gallery.photos.map((photo, idx) => (
                <div key={idx} className="w-full h-full">
                  <img
                    src={photo.photo_url}
                    alt={`Photo ${idx + 1}`}
                    className="object-cover w-full h-64 md:h-80"
                  />
                </div>
              ))}
            </Slider>
          ) : (
            <img
              src={gallery.photos[0].photo_url}
              alt="Gallery cover"
              className="object-cover w-full h-64 md:h-80"
            />
          )
        ) : (
          <img
            src={defaultImage}
            alt="Default gallery cover"
            className="object-cover w-full h-64 md:h-80"
          />
        )}

        {/* Badge actif / archivé */}
        <div className="absolute top-3 left-3">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.16em] ${
              gallery.is_active
                ? "bg-emerald-500 text-white"
                : "bg-slate-700 text-slate-100"
            }`}
          >
            {gallery.is_active ? <FaUnlock /> : <FaLock />}
            {gallery.is_active ? "Actif" : "Archivé"}
          </span>
        </div>

        {/* Nombre de photos */}
        <div className="absolute bottom-3 right-3">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-semibold bg-black/70 text-white">
            <FaImages className="text-xs" />
            {gallery.photos?.length || 0} photo
            {gallery.photos && gallery.photos.length > 1 && "s"}
          </span>
        </div>
      </div>

      {/* Bloc texte / actions */}
      <div className="md:w-1/2 p-5 flex flex-col justify-between bg-slate-50/80 dark:bg-slate-900/80">
        <div>
          <h3 className="text-lg md:text-xl font-bold text-slate-800 dark:text-white font-montserrat mb-2">
            {gallery.titre}
          </h3>
          {gallery.category && (
            <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
              {gallery.category}
            </p>
          )}
          <div
            className="text-sm text-slate-700 dark:text-slate-200 mb-3 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: gallery.description }}
          />
        </div>

        <div className="flex flex-wrap gap-3 justify-center md:justify-end mt-2">
          <Button
            text="Voir l'Album"
            onClick={handleViewAlbumClick}
            className="gap-2 text-sm"
            icon={FaImages}
          />
          {isAdmin && (
            <>
              <Button
                text="Éditer"
                onClick={() => setEditModalOpen(true)}
                className="gap-2 text-sm bg-blue-600 hover:bg-blue-700 text-white"
                icon={FaEdit}
              />
              <Button
                text="Supprimer"
                onClick={handleDeleteGallery}
                className="gap-2 text-sm bg-rose-600 hover:bg-rose-700 text-white"
                icon={FaTrashAlt}
              />
            </>
          )}
        </div>
      </div>

      {editModalOpen && (
        <EditAlbumModal
          gallery={gallery}
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          refreshList={refreshGalleriesList}
        />
      )}
    </article>
  );
};

export default GalleryCard;
